import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AddressCombobox, addressLookupId, addressLookupLabel } from '../components/AddressCombobox';
import { ErrorBox, Loading } from '../components/Feedback';
import { ModuleHeader } from '../components/ModuleHeader';
import { Pager } from '../components/Pager';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { CheckItem, LookupItem } from '../types';

function rows(payload: any): CheckItem[] { return Array.isArray(payload) ? payload : payload?.items || payload?.rows || []; }
function lookupRows(payload: any): LookupItem[] { return Array.isArray(payload) ? payload : payload?.items || payload?.rows || []; }
function addressLabel(item: CheckItem): string { const address = item.address || {}; return item.addressName || String(address.name || address.denumire || address.address || address.adresa || 'Locație'); }

export function ChecksPage() {
  const { can } = useAuth();
  const [items, setItems] = useState<CheckItem[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [locations, setLocations] = useState<LookupItem[]>([]);
  const [addressId, setAddressId] = useState('');
  const [filteredAddressId, setFilteredAddressId] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void api.locations(locationSearch.trim()).then((result) => setLocations(lookupRows(result))).catch(() => setLocations([]));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [locationSearch]);

  useEffect(() => {
    setLoading(true);
    void api.checks({ page, limit: 25, addressId: filteredAddressId }).then((result) => {
      const values = rows(result).sort((a, b) => String(b.performedAt || '').localeCompare(String(a.performedAt || '')));
      setItems(values);
      setTotal(Number(result?.total ?? values.length));
      setError('');
    }).catch((reason) => setError(reason instanceof Error ? reason.message : 'Verificările nu pot fi încărcate.')).finally(() => setLoading(false));
  }, [filteredAddressId, page]);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (locationSearch.trim() && !addressId) {
      setError('Selectați adresa din lista de rezultate.');
      return;
    }
    setPage(1);
    setFilteredAddressId(addressId);
  }

  return <div className="page"><ModuleHeader title="Check" subtitle={`${total} verificări`} /><main className="page-body module-body">
    <form className="card filter-card check-location-filter" onSubmit={submit}>
      <AddressCombobox label="Caută adresa" value={locationSearch} selectedId={addressId} items={locations} onSearchChange={(value) => { setLocationSearch(value); setAddressId(''); }} onSelect={(address) => { setAddressId(addressLookupId(address)); setLocationSearch(addressLookupLabel(address)); }} />
      <button className="btn btn-secondary block">Aplică filtrul</button>
    </form>
    {can('check', 'write') && <Link className="btn btn-primary block" to="/checks/new">+ Verificare nouă</Link>}
    {loading && <Loading />}{error && <ErrorBox message={error} />}
    {!loading && !items.length && <div className="empty"><strong>Nicio verificare</strong><span>Nu există rezultate pentru adresa selectată.</span></div>}
    <section className="entity-list">{items.map((item) => { const id = item._id || item.id || item.checkId || ''; return <Link className="entity-card" key={id} to={`/checks/${encodeURIComponent(id)}`}><div className="entity-card__top"><span className="status-badge status-badge--check">Check</span><small>{item.performedAt ? new Intl.DateTimeFormat('ro-RO', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(item.performedAt)) : 'Fără dată'}</small></div><h2>{addressLabel(item)}</h2><p>{item.observations || 'Fără observații'}</p><div className="entity-meta"><span>{item.ownerName || item.owner || '—'}</span><span>{item.responsibleIds?.length || item.assignedUsers?.length || 0} alocați</span></div></Link>; })}</section>
    <Pager page={page} total={total} limit={25} onChange={setPage} />
  </main></div>;
}
